/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { isObservable } from '@gh/core-util';
import { distinctUntilChanged$ } from '@gh/rx/operators';
import { Column, Events, GridApi, GridOptions, RowNode } from 'ag-grid-community';
import { isNil, isString, property } from 'lodash';
import { Subscription } from 'rxjs';
import { GridSelectedRowChangeEvent } from '../../grid-events';
import { createSimpleGridModel } from '../../state/simple-grid-state';
import { FocusedCell, GridModel } from '../../state';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';

@Directive({
  selector: '[ghGridModelData],[ghGridImmutableData]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridModelDataDirective,
    multi: true,
  }],
  // We define all inputs via @Directive decorator, because we have to handle all inputs in order.
  // We use ngOnChanges to handle all inputs in order.
  // tslint:disable-next-line:use-input-property-decorator
  inputs: [
    'modelData:ghGridModelData',
    'immutableData:ghGridImmutableData',
    'focusedCell',
    'selectedRow',
  ],
})
export class GridModelDataDirective implements GridFeature, OnChanges {
  /**
   * Easy way to set identity property.
   */
  @Input('ghGridImmutableDataId') idProperty: string | TransformFn<any, any>;

  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() selectedRowChange = new EventEmitter<GridSelectedRowChangeEvent<any>>();

  order = 10;

  private _focusedCell: FocusedCell;
  private _selectedRow: any;
  private _data: any;
  private model: GridModel;
  private api: GridApi;

  private dataSubscription?: Subscription;
  private editableSubscription?: Subscription;
  private initialized: boolean = false;
  private modelBasedMode = true;

  /**
   * This flag indicates that selection was requested by setting input parameter,
   * but was not applied to the grid yet.
   * This is necessary to distinguish model-based requests to select row from user-based (like click on a row).
   * We have to guarantee one-way data flow without any side effect.
   * So we always have to take selected row from the model.
   */
  private selectionInProgress: boolean = false;

  setData(data: any): void {
    this._data = data || [];

    if (this.initialized) {
      this.model.setData(this._data);
    }
  }

  setFocusedCell(cell: FocusedCell): void {
    if (cell !== this._focusedCell) {
      this._focusedCell = cell;

      this.goToFocusedCell();
    }
  }

  setSelectedRow(id: any): void {
    if (id !== this._selectedRow) {
      this._selectedRow = id;

      this.goToSelectedRow();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { immutableData, modelData, focusedCell, selectedRow } = changes;
    if (!this.initialized) {
      if (immutableData && modelData) {
        throw new Error('GridModelDataDirective: this directive accepts data ' +
          'either from ghGridModelData or ghGridImmutableData, but not from both');
      } else if (immutableData) {
        this.modelBasedMode = false;
      }
    }
    const data = immutableData || modelData;
    if (data) {
      this.setData(data.currentValue);
    }
    if (focusedCell) {
      this.setFocusedCell(focusedCell.currentValue);
    }
    if (selectedRow) {
      this.setSelectedRow(selectedRow.currentValue);
    }
  }

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    if (this.modelBasedMode) {
      const model: GridModel = options.context;
      if (isNil(model) || !isObservable(model.rowData)) {
        throw new Error('GridModelDataDirective: GridModel is required by ghGridModelData directive. ' +
          'Setup GridModel using Model-based GridDefinition');
      }
      this.model = model;
    } else if (this.idProperty) {
      const getId = isString(this.idProperty) ? property(this.idProperty) : this.idProperty;
      this.model = createSimpleGridModel(getId);
      // @ts-ignore
      options.getRowNodeId = getId;
    } else if (options.getRowNodeId) {
      this.model = createSimpleGridModel(options.getRowNodeId);
    } else {
      throw new Error('GridModelDataDirective: GridOptions.getRowNodeId should be implemented ' +
        'when immutable data is provided');
    }

    this.dataSubscription = this.model.rowData
      .subscribe((rowData: any[]) => {
        if (this.initialized) {
          this.api.setRowData(rowData);
        } else {
          options.rowData = rowData;
        }
      });

    options.deltaRowDataMode = true;

    this.model.setData(this._data);
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
    this.initialized = true;

    const { model, api } = this;

    api.addEventListener(Events.EVENT_ROW_SELECTED, this.onRowSelected.bind(this));

    this.editableSubscription = model.editable.pipe(
      distinctUntilChanged$())
      .subscribe(() => api.refreshCells());

    this.bindEvents();

    this.goToFocusedCell();
    this.goToSelectedRow();
  }

  onDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.editableSubscription) {
      this.editableSubscription.unsubscribe();
    }
  }

  private goToFocusedCell(): void {
    const { api, _focusedCell: cell } = this;

    if (cell && api) {
      const node = api.getRowNode(cell.id); // tslint:disable-line:no-non-null-assertion
      if (node) {
        api.ensureIndexVisible(node.rowIndex);
        api.setFocusedCell(node.rowIndex, cell.field);
      }
    }
  }

  private goToSelectedRow(): void {
    const { api, _selectedRow: row } = this;

    if (row && api) {
      const node = api.getRowNode(row); // tslint:disable-line:no-non-null-assertion
      if (node && !node.isSelected()) {
        // wait until selection will be handled by grid
        this.selectionInProgress = true;
        node.setSelected(true);
        api.ensureIndexVisible(node.rowIndex);
      }
    }
  }

  private bindEvents(): void {
    const { api, model } = this;
    api.addEventListener('cellFocused', ({ rowIndex, column }: { rowIndex: number; column: Column }) => {
      const node = api.getModel().getRow(rowIndex);

      if (isNil(node)) {
        // can be null for pinned rows
        // currently focusing of pinned rows is not supported
        return;
      }

      const id = model.getEntityId(node.data);
      const field = column.getColDef().field!; // tslint:disable-line:no-non-null-assertion

      const cell = this._focusedCell = { id, field };
      this.focusedCellChange.emit(cell);
    });
  }

  private onRowSelected({ node }: { node: RowNode }): void {
    if (this.selectionInProgress) {
      // selection was handled by grid => typical grid selection flow can continue now.
      this.selectionInProgress = false;
      return;
    }
    if (node.isSelected()) {
      const { _selectedRow: selectedRow } = this;
      if (node.id !== selectedRow) {
        // when selection was modified we emit event
        this.selectedRowChange.emit({ id: node.id, node, data: node.data });
        // always reset selection to relay on one-way data flow (avoid any side effects)
        if (selectedRow) {
          this.api.getRowNode(selectedRow).setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    } else {
      const rowModel = this.api.getModel();
      const nextNode = rowModel.getRow(node.rowIndex) || rowModel.getRow(node.rowIndex - 1);
      if (nextNode && nextNode !== node) {
        nextNode.setSelected(true);
      } else if (isNil(nextNode)) {
        this.selectedRowChange.emit({});
      }
    }
  }
}
